import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { App, Button, Drawer, Input, Modal, Popconfirm, Typography } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { Coins, Gift, Mail, Search, UserCog, UserPlus } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

import { useBreakpoint, useDrawerSurface } from '@/hooks';
import { useUserStore } from '@/store/userStore';
import { DataTable } from '@/components/DataTable';
import { BuilderIconButton } from '@/components/BuilderIconButton';
import { InviteUserForm } from '@/components/InviteUserForm';
import { ManagePackagesForm } from '@/components/ManagePackagesForm';
import { AddCreditsForm } from '@/components/AddCreditsForm';
import { ChangeRoleForm } from '@/components/ChangeRoleForm';
import { AdminService } from '@/services/adminService';
import { parseErrorMsg } from '@/services/backend';
import { AdminAssessmentPackagesService, BaseAPIClient, type AssessmentPackage } from '@/services/api';
import { USER_STATUS, type AdminUser, type AdminUserPackage } from '@/types/user';
import { USER_ROLE_LABELS, type UserRole } from '@/utils/roles';
import { roleStyle } from './userBadges';
import { UserDetailSheet } from './UserDetailSheet';
import { DrawerHeader } from '@/components/DrawerHeader';
import { formatUserName } from '@/utils/formatters';
import { ADMIN_TABLE_PAGE_SIZE } from '@/utils/constants/admin';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { ROUTES } from '@/routes/routes';
import styles from './AdminUsers.module.scss';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' as const } },
};

type SortField = 'name' | 'email' | 'role' | 'status';
type SortOrder = 'ascend' | 'descend' | null;

const buildColumns = (
  currentUserId: number | string,
  resendingId: number | null,
  onResend: (user: AdminUser) => void,
  onManagePackages: (user: AdminUser) => void,
  onAddCredits: (user: AdminUser) => void,
  onChangeRole: (user: AdminUser) => void,
  sortField: SortField | null,
  sortOrder: SortOrder,
): ColumnsType<AdminUser> => [
  {
    title: 'Name',
    key: 'name',
    width: 160,
    ellipsis: true,
    sorter: (_a, _b) => 0,
    sortOrder: sortField === 'name' ? sortOrder : null,
    render: (_, record) => {
      const name = formatUserName(record.firstName, record.lastName);
      return name || <Typography.Text type="secondary">—</Typography.Text>;
    },
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    width: 220,
    ellipsis: true,
    sorter: (_a, _b) => 0,
    sortOrder: sortField === 'email' ? sortOrder : null,
  },
  {
    title: 'Role',
    dataIndex: 'role',
    key: 'role',
    width: 90,
    sorter: (_a, _b) => 0,
    sortOrder: sortField === 'role' ? sortOrder : null,
    render: (role: UserRole) => (
      <span className={`${styles.roleBadge} ${roleStyle(role)}`}>{USER_ROLE_LABELS[role]}</span>
    ),
  },
  {
    title: 'Status',
    key: 'status',
    width: 90,
    sorter: (_a, _b) => 0,
    sortOrder: sortField === 'status' ? sortOrder : null,
    render: (_, record) => {
      const active = record.status === USER_STATUS.ACTIVE;
      return (
        <span className={`${styles.statusBadge} ${active ? styles.active : styles.pending}`}>
          <span className={styles.dot} />
          {active ? 'Active' : 'Pending'}
        </span>
      );
    },
  },
  {
    title: 'Credits',
    dataIndex: 'balance',
    key: 'balance',
    width: 90,
    render: (balance: number) => (
      <span className={styles.credits}>
        {balance.toLocaleString()}
      </span>
    ),
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 160,
    render: (_, record) => {
      const canActOnTarget = record.id !== currentUserId;
      const canResend = record.status === USER_STATUS.PENDING && canActOnTarget;
      return (
        <div className={styles.rowActions}>
          <BuilderIconButton
            icon={<Gift style={{ width: '1em', height: '1em' }} />}
            title="Manage packages"
            onClick={() => onManagePackages(record)}
          />
          <BuilderIconButton
            icon={<Coins style={{ width: '1em', height: '1em' }} />}
            title="Add credits"
            onClick={() => onAddCredits(record)}
          />
          <BuilderIconButton
            icon={<UserCog style={{ width: '1em', height: '1em' }} />}
            title="Change role"
            disabled={!canActOnTarget}
            onClick={() => onChangeRole(record)}
          />
          <Popconfirm
            title="Resend invite?"
            description={`A new invitation will be sent to ${record.email}`}
            onConfirm={() => onResend(record)}
            okText="Send"
            cancelText="Cancel"
            okButtonProps={{ shape: 'round' }}
            cancelButtonProps={{ shape: 'round' }}
            disabled={!canResend}
          >
            <BuilderIconButton
              icon={<Mail style={{ width: '1em', height: '1em' }} />}
              title="Resend invite"
              disabled={!canResend}
              loading={resendingId === record.id}
            />
          </Popconfirm>
        </div>
      );
    },
  },
];

export const AdminUsers: React.FC = () => {
  const { user } = useUserStore();
  const { getToken } = useAuth();
  const { notification, modal } = App.useApp();
  const { isMobile } = useBreakpoint();
  const drawerSurface = useDrawerSurface();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [viewingUser, setViewingUser] = useState<AdminUser | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [packages, setPackages] = useState<AssessmentPackage[] | null>(null);
  const [managingUser, setManagingUser] = useState<AdminUser | null>(null);
  const [managingUserPackages, setManagingUserPackages] = useState<AdminUserPackage[] | null>(null);
  // Who the Manage packages modal is open for (null when closed); stale loads are dropped.
  const managingUserIdRef = useRef<number | null>(null);
  const [creditingUser, setCreditingUser] = useState<AdminUser | null>(null);
  const [roleChangingUser, setRoleChangingUser] = useState<AdminUser | null>(null);

  const { searchInput, handleSearchChange } = useDebouncedSearch((val) => {
    setPage(1);
    setSearch(val);
  });

  const getService = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error('Not authenticated');
    return new AdminService(token);
  }, [getToken]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const svc = await getService();
      const apiSortOrder = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      const result = await svc.getUsers(
        page,
        ADMIN_TABLE_PAGE_SIZE,
        search || undefined,
        sortField ?? undefined,
        apiSortOrder,
      );
      setUsers(result.data);
      setTotalCount(result.pagination.totalCount);
    } catch (err) {
      notification.error({ message: parseErrorMsg(err, 'Failed to load users') });
    } finally {
      setLoading(false);
    }
  }, [page, search, sortField, sortOrder, getService, notification]);

  const handleTableChange: NonNullable<TableProps<AdminUser>['onChange']> = useCallback(
    (_pagination, _filters, sorter) => {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      setSortField((s?.columnKey as SortField | undefined) ?? null);
      setSortOrder(s?.order ?? null);
      setPage(1);
    },
    [],
  );

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const loadPackages = async () => {
      try {
        const token = await getToken();
        if (!token) throw new Error('Not authenticated');
        const svc = new AdminAssessmentPackagesService(token);
        setPackages(await svc.list());
      } catch (err) {
        notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
      }
    };
    void loadPackages();
  }, [getToken, notification]);

  if (!user) return null;

  const handleAdd = async (values: {
    firstName: string;
    lastName: string;
    email: string;
    role: UserRole;
  }) => {
    try {
      const svc = await getService();
      const created = await svc.inviteUser(
        values.firstName.trim(),
        values.lastName?.trim() || null,
        values.email.trim(),
        values.role,
        `${window.location.origin}${ROUTES.REGISTER}`,
      );
      setIsAddModalOpen(false);
      notification.success({
        // ACTIVE = the email already had a login, so it was linked rather than invited.
        message:
          created.status === USER_STATUS.ACTIVE
            ? `${created.email} already had a login. Linked their account, so they can sign in now.`
            : `Invite sent to ${values.email.trim()}`,
      });
      if (page === 1) void fetchUsers();
      else setPage(1);
    } catch (err) {
      notification.error({ message: parseErrorMsg(err, 'Failed to invite user') });
      throw err; // keep the modal open and preserve the entered values
    }
  };

  const handleResend = async (targetUser: AdminUser) => {
    setResendingId(targetUser.id);
    try {
      const svc = await getService();
      await svc.resendInvite(targetUser.id, `${window.location.origin}${ROUTES.REGISTER}`);
      notification.success({ message: `Invite resent to ${targetUser.email}` });
    } catch (err) {
      notification.error({ message: parseErrorMsg(err, 'Failed to resend invite') });
    } finally {
      setResendingId(null);
    }
  };

  const loadManagingUserPackages = async (userId: number) => {
    try {
      const svc = await getService();
      const { ownedPackages, balance } = await svc.getUserPackages(userId);
      if (managingUserIdRef.current !== userId) return;
      setManagingUserPackages(ownedPackages);
      // Fresher than the users-list balance, and what creditsToRevoke was capped against.
      setManagingUser((prev) => (prev?.id === userId ? { ...prev, balance } : prev));
    } catch (err) {
      notification.error({ message: parseErrorMsg(err, 'Failed to load owned packages') });
    }
  };

  const handleOpenManagePackages = async (targetUser: AdminUser) => {
    managingUserIdRef.current = targetUser.id;
    setManagingUser(targetUser);
    setManagingUserPackages(null);
    await loadManagingUserPackages(targetUser.id);
  };

  const handleCloseManagePackages = () => {
    managingUserIdRef.current = null;
    setManagingUser(null);
  };

  // Both keep the modal open and refresh it in place.
  const handleGrantPackage = async (packageId: string) => {
    if (!managingUser) return;
    try {
      const svc = await getService();
      const { balance } = await svc.grantPackage(managingUser.id, packageId);
      notification.success({ message: `Package granted to ${managingUser.email}` });
      // Functional, so a modal closed while this was in flight stays closed.
      setManagingUser((prev) => (prev?.id === managingUser.id ? { ...prev, balance } : prev));
      await loadManagingUserPackages(managingUser.id);
      void fetchUsers();
    } catch (err) {
      notification.error({ message: parseErrorMsg(err, 'Failed to grant package') });
      throw err;
    }
  };

  const handleRevokePackage = async (packageId: string) => {
    if (!managingUser) return;
    try {
      const svc = await getService();
      const { balance } = await svc.revokePackage(managingUser.id, packageId);
      notification.success({ message: `Package removed from ${managingUser.email}` });
      setManagingUser((prev) => (prev?.id === managingUser.id ? { ...prev, balance } : prev));
      await loadManagingUserPackages(managingUser.id);
      void fetchUsers();
    } catch (err) {
      notification.error({ message: parseErrorMsg(err, 'Failed to remove package') });
      throw err;
    }
  };

  const handleAddCredits = async (values: { amount: number }) => {
    if (!creditingUser) return;
    try {
      const svc = await getService();
      await svc.addCredits(creditingUser.id, values.amount);
      notification.success({ message: `Added ${values.amount.toLocaleString()} credits to ${creditingUser.email}` });
      setCreditingUser(null);
      void fetchUsers();
    } catch (err) {
      notification.error({ message: parseErrorMsg(err, 'Failed to add credits') });
      throw err; // keep the modal open and preserve the entered values
    }
  };

  const handleChangeRole = async (values: { role: UserRole }) => {
    if (!roleChangingUser) return;
    try {
      const svc = await getService();
      await svc.updateUser(roleChangingUser.id, { role: values.role });
      notification.success({
        message: `${roleChangingUser.email} is now ${USER_ROLE_LABELS[values.role]}`,
      });
      setRoleChangingUser(null);
      void fetchUsers();
    } catch (err) {
      notification.error({ message: parseErrorMsg(err, 'Failed to change role') });
      throw err; // keep the modal open and preserve the entered values
    }
  };

  const columns = buildColumns(
    user.id,
    resendingId,
    handleResend,
    handleOpenManagePackages,
    (targetUser) => setCreditingUser(targetUser),
    (targetUser) => setRoleChangingUser(targetUser),
    sortField,
    sortOrder,
  );

  return (
    <motion.div
      className={styles.page}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className={styles.header} variants={itemVariants}>
        <div>
          <Typography.Title level={2} className={styles.title}>
            Manage Users
          </Typography.Title>
          <Typography.Text type="secondary" className={styles.pageSub}>
            View and manage everyone with access to this application.
          </Typography.Text>
        </div>

        <Button
          type="primary"
          shape="round"
          icon={isMobile ? undefined : <UserPlus style={{ width: '1em', height: '1em' }} />}
          onClick={() => setIsAddModalOpen(true)}
        >
          {isMobile ? 'Add' : 'Add User'}
        </Button>
      </motion.div>

      <motion.div className={styles.toolbar} variants={itemVariants}>
        <Input
          placeholder="Search by name or email"
          value={searchInput}
          onChange={handleSearchChange}
          allowClear
          prefix={<Search style={{ width: '1em', height: '1em' }} />}
          className={styles.searchInput}
        />
      </motion.div>

      <motion.div variants={itemVariants}>
        <DataTable
          dataSource={users}
          columns={columns}
          rowKey="id"
          loading={loading}
          page={page}
          totalCount={totalCount}
          pageSize={ADMIN_TABLE_PAGE_SIZE}
          onPageChange={setPage}
          onChange={handleTableChange}
          mobileTitle={(record) => formatUserName(record.firstName, record.lastName) || record.email}
          mobileActionsLabel={(record) =>
            `Actions for ${formatUserName(record.firstName, record.lastName) || record.email}`
          }
          onMobileDetails={(record) => setViewingUser(record)}
        />
      </motion.div>

      <Drawer
        title={<DrawerHeader title="User" onClose={() => setViewingUser(null)} />}
        closable={false}
        open={Boolean(viewingUser)}
        onClose={() => setViewingUser(null)}
        {...drawerSurface}
        destroyOnHidden
        className={styles.drawer}
      >
        {viewingUser && (
          <UserDetailSheet
            user={viewingUser}
            isSelf={viewingUser.id === user.id}
            resending={resendingId === viewingUser.id}
            onManagePackages={() => {
              handleOpenManagePackages(viewingUser);
              setViewingUser(null);
            }}
            onAddCredits={() => {
              setCreditingUser(viewingUser);
              setViewingUser(null);
            }}
            onChangeRole={() => {
              setRoleChangingUser(viewingUser);
              setViewingUser(null);
            }}
            onResend={() =>
              modal.confirm({
                title: 'Resend invite?',
                icon: null,
                maskClosable: true,
                centered: true,
                content: `A new invitation will be sent to ${viewingUser.email}`,
                okText: 'Send',
                cancelText: 'Cancel',
                okButtonProps: { shape: 'round' },
                cancelButtonProps: { shape: 'round' },
                onOk: async () => {
                  await handleResend(viewingUser);
                  setViewingUser(null);
                },
              })
            }
          />
        )}
      </Drawer>

      <Modal
        title="Add user"
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        centered
        footer={null}
        width={440}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        destroyOnHidden
      >
        <p className={styles.modalDescription}>
          Send an invite email so a new user can create their account and access the app.
        </p>
        <InviteUserForm onSubmit={handleAdd} onCancel={() => setIsAddModalOpen(false)} />
      </Modal>

      <Modal
        title="Manage packages"
        open={!!managingUser}
        onCancel={handleCloseManagePackages}
        centered
        footer={null}
        width={720}
        styles={{
          container: { display: 'flex', flexDirection: 'column', maxHeight: '85vh' },
          body: { flex: '1 1 auto', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
        }}
        destroyOnHidden
      >
        {managingUser && (
          <ManagePackagesForm
            userLabel={formatUserName(managingUser.firstName, managingUser.lastName) || managingUser.email}
            currentBalance={managingUser.balance}
            packages={packages}
            ownedPackages={managingUserPackages}
            onGrant={handleGrantPackage}
            onRevoke={handleRevokePackage}
            onCancel={handleCloseManagePackages}
          />
        )}
      </Modal>

      <Modal
        title="Add credits"
        open={!!creditingUser}
        onCancel={() => setCreditingUser(null)}
        centered
        footer={null}
        width={420}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        destroyOnHidden
      >
        {creditingUser && (
          <AddCreditsForm
            userLabel={formatUserName(creditingUser.firstName, creditingUser.lastName) || creditingUser.email}
            currentBalance={creditingUser.balance}
            onSubmit={handleAddCredits}
            onCancel={() => setCreditingUser(null)}
          />
        )}
      </Modal>

      <Modal
        title="Change role"
        open={!!roleChangingUser}
        onCancel={() => setRoleChangingUser(null)}
        centered
        footer={null}
        width={420}
        destroyOnHidden
      >
        {roleChangingUser && (
          <ChangeRoleForm
            userLabel={formatUserName(roleChangingUser.firstName, roleChangingUser.lastName) || roleChangingUser.email}
            currentRole={roleChangingUser.role}
            onSubmit={handleChangeRole}
            onCancel={() => setRoleChangingUser(null)}
          />
        )}
      </Modal>
    </motion.div>
  );
};

export default AdminUsers;
